import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PersonLineDisplay from "../../Shared/PersonLineDisplay";
import TaskStatus from "../../Shared/TaskStatus";
import TaskPriority from "../../Shared/TaskPriority";
import TaskType from "../../Shared/TaskType";
import AddButton from "../../buttons/btnAdd.component/BtnAdd";
import Search from "../../UiLib/Search/Search";
import SortDirectionArrow from "../../../assets/sortDirectionArrow.svg";
import "./TasksWidget.css";
import "../../../globalStyles/dataTableStyles.css";
import { ProjectContext } from "../../../pages/Project/Project";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fullNameFormat } from "../../../helpers/Inicials";
import OpenBlock from "../../../assets/openBlock.svg";
import CloseBlockWindow from "../../../assets/arrowCloseWindow.svg";
import TaskAddEditForm from "../../Task/TaskAddEditForm/TaskAddEditForm";
import { AppContext } from "../../../App";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import UserPopupWrapper from "../../Shared/UserPopup/UserPopupWrapper";
import FilterContainer from "../../Shared/Filter/FilterContainer";
import TaskLink from "./TaskLink";
import { hasPermission } from "../../../helpers/hasPermisson";
import TaskColumn from "../../Shared/TaskColumn";
import { dateToNiceString } from "../../../helpers/dateToNiceString";
import { getOptionsQueryParams } from "../../../helpers/paramsAutoFiller";
import { ITaskDisplay } from "../../../common/api_interfaces/task/ITaskDisplay.response";
import { IMonitorTaskDisplay } from "../../../common/api_interfaces/monitor/IUserMonitorTaskFullInfo";

export const PAGE_SIZE = 20;
// Число отображаемых задач для полностью развёрнутого экрана с задачами на странице проекта
const FULL_SCREEN_PAGE_SIZE = 40;

interface ISort {
    date?: "asc" | "desc";
    status?: "asc" | "desc";
    responsible?: "asc" | "desc";
    type?: "asc" | "desc";
    priority?: "asc" | "desc";
    nameTask?: "asc" | "desc";
}

interface ITasksProps {
    tasks: ITaskDisplay[];
    scrollEnded: boolean;
    loadPage: (skip: number, take: number, sort: ISort) => void;
    loadTasks: (skip: number, take: number, sort: ISort) => void;
    userAccess: IFunctionPermissions[];
}

export const personRowDisplay = (rowData: ITaskDisplay) => {
    if (!rowData.responsible) return null;
    const initials = fullNameFormat(
        { ...rowData.responsible, middlename: rowData.responsible?.middlename ?? "" },
        "s N M"
    );
    return (
        <UserPopupWrapper userId={rowData.responsible.id}>
            <PersonLineDisplay name={initials} userId={rowData.responsible.id} />
        </UserPopupWrapper>
    );
};
export const taskStatusDisplay = (rowData: ITaskDisplay | IMonitorTaskDisplay) => {
    const style = rowData?.status?.style && JSON.parse(rowData?.status?.style);
    return style && rowData?.status?.name ? <TaskStatus name={rowData?.status?.name} color={style.color} /> : "";
};

const taskColumnDisplay = (task: ITaskDisplay) => {
    return (
        <div className="column-display-container">
            <div>
                <TaskColumn name={task.columns.main?.name} color={task.columns.main?.color} />
            </div>
            {task.columns.proxy ? <TaskColumn name={task.columns.proxy.name} color={task.columns.proxy.color} /> : null}
        </div>
    );
};

export const taskPriorityDisplay = (rowData: ITaskDisplay | IMonitorTaskDisplay) => {
    const style = rowData?.priority?.style && JSON.parse(rowData?.priority?.style);
    return style && rowData?.priority?.name ? (
        <TaskPriority name={rowData?.priority?.name} color={style.color} icon={style.icon} />
    ) : (
        ""
    );
};

export const taskTypeDisplay = (rowData: ITaskDisplay | IMonitorTaskDisplay) => {
    const style = rowData?.type?.style && JSON.parse(rowData?.type?.style);
    return style && rowData?.type?.name ? (
        <TaskType name={rowData?.type?.name} color={style.color} icon={style.icon} />
    ) : (
        ""
    );
};

type Header = {
    lable: string;
    code: string;
    sortable: boolean;
    showOnlyOnFullScreen: boolean;
};

const headers: Header[] = [
    {
        lable: "НОМЕР",
        code: "idForView",
        sortable: true,
        showOnlyOnFullScreen: false,
    },
    {
        lable: "НАЗВАНИЕ ЗАДАЧИ",
        code: "nameTask",
        sortable: true,
        showOnlyOnFullScreen: false,
    },
    { lable: "СТАТУС", code: "status", sortable: true, showOnlyOnFullScreen: false },
    { lable: "КОЛОНКА", code: "columnTask", sortable: false, showOnlyOnFullScreen: false },
    {
        lable: "ПРИОРИТЕТ",
        code: "priority",
        sortable: true,
        showOnlyOnFullScreen: false,
    },
    { lable: "ТИП ЗАДАЧИ", code: "type", sortable: true, showOnlyOnFullScreen: true },
    {
        lable: "ИСПОЛНИТЕЛЬ",
        code: "responsible",
        sortable: true,
        showOnlyOnFullScreen: false,
    },
    { lable: "ДАТА", code: "date", sortable: true, showOnlyOnFullScreen: true },
];

const Tasks: React.FC<ITasksProps> = ({ tasks, scrollEnded, loadPage, loadTasks, userAccess }) => {
    const { id } = useParams();
    const {
        setCounterTasks,
        sortAsc,
        setSortAsc,
        filterOptionsTask,
        counterTasks,
        selectedOptionsTask,
        setSelectedOptionsTask,
        taskSearchText,
        setTaskSearchText,
        skipTasks,
        setSkipTasks,
        setScrollEndedTasks,
        projectSettings,
    } = useContext(ProjectContext);
    const { showToast, archivedPage } = useContext(AppContext);

    const [hidden, setHidden] = useState(false);
    const [openOnFullWindow, setOpenOnFullWindow] = useState(false);

    const [currentSort, setCurrentSort] = useState<string>();

    const currentSortObj: ISort = currentSort ? { [currentSort]: sortAsc ? "asc" : "desc" } : {};

    const curRef = useRef(null);

    // Ограничение прав
    const viewTasks = useMemo(() => hasPermission(userAccess, "TaskAction", "view"), [userAccess]);
    const addTask = useMemo(() => hasPermission(userAccess, "TaskAction", "create"), [userAccess]);

    const onScroll = async (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;
        if (1 + scrollTop + offsetHeight >= scrollHeight && !scrollEnded) {
            const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : PAGE_SIZE;
            setSkipTasks((prev: number) => prev + pageSize);
            await loadPage(skipTasks, pageSize, currentSortObj);
        }
    };

    useEffect(() => {
        let options = getOptionsQueryParams(window.location.search);
        if (options.page === "project" && options.entity === "task-form" && options.action === "create") {
            setHidden(true);
        }
    }, [userAccess]);

    useEffect(() => {
        setOpenOnFullWindow(false);
    }, [id]);

    useEffect(() => {
        const keyDownHandler = (event: any) => {
            if (event.key === "Escape") {
                event.preventDefault();
                setTaskSearchText("");
                onCancelClick();
            }
        };
        document.addEventListener("keydown", keyDownHandler);

        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, []);

    useEffect(() => {
        const widget = containerRef.current;

        if (openOnFullWindow && widget) {
            widget?.scrollIntoView({ behavior: "auto", block: "center" });
        }

        (async () => {
            // Если перешли в полноэкранный режим, догружаем задачи до полной страницы
            if (openOnFullWindow && tasks.length < FULL_SCREEN_PAGE_SIZE) {
                await loadTasks(0, FULL_SCREEN_PAGE_SIZE, currentSortObj);
                setSkipTasks(FULL_SCREEN_PAGE_SIZE);
            }
        })();
    }, [openOnFullWindow]);

    useEffect(() => {
        if (taskSearchText.length === 0 && Object.keys(selectedOptionsTask).length === 0) {
            (async () => {
                setScrollEndedTasks(false);
                const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : PAGE_SIZE;
                setSkipTasks(pageSize);
                await loadTasks(0, pageSize, currentSortObj);
            })();
        }
    }, [taskSearchText, selectedOptionsTask]);

    async function onCancelClick() {
        setTaskSearchText("");
        setSelectedOptionsTask({});
    }

    function onAddClick() {
        if (addTask) {
            setHidden(true);
        } else {
            showToast("У вас нет прав для создания задачи!");
        }
    }

    async function changeSort(code: string | undefined, asc: boolean) {
        const foundSort = headers.find((s) => s.code === code);
        if (code && !foundSort?.sortable) return;
        (curRef.current as any).scrollTo({
            top: 0,
            left: 0,
        });
        setCurrentSort(code);
        const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : PAGE_SIZE;
        setSkipTasks(pageSize);
        await loadTasks(0, pageSize, code ? { [code]: asc ? "asc" : "desc" } : {});
        setSortAsc(asc);
    }

    async function onSearchSubmit(value: string) {
        setScrollEndedTasks(false);
        const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : PAGE_SIZE;
        setSkipTasks(pageSize);
        await loadTasks(0, pageSize, currentSortObj);
    }

    function onTaskAction() {
        setHidden(false);
        (async () => {
            const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : PAGE_SIZE;
            await loadTasks(0, pageSize, currentSortObj);
        })();
    }

    const applyFilters = async () => {
        setScrollEndedTasks(false);
        const pageSize = openOnFullWindow ? FULL_SCREEN_PAGE_SIZE : PAGE_SIZE;
        setSkipTasks(pageSize);
        await loadTasks(0, pageSize, currentSortObj);
    };

    const containerRef = useRef<HTMLDivElement>(null);

    function columnHead(header: Header) {
        return currentSort === header.code ? (
            <th onClick={() => changeSort(sortAsc ? header.code : undefined, !sortAsc)}>
                <div
                    className={`no-highlight sort-direction-changing-header ${sortAsc ? "" : "desc"} ${
                        header.sortable ? "sortable" : null
                    } sort-direction-changing-header-flex`}
                >
                    {header.lable}
                    <img src={SortDirectionArrow} />
                </div>
            </th>
        ) : (
            <th onClick={() => changeSort(header.code, true)}>
                <div
                    id={header.code}
                    className={`sort-direction-changing-header ${
                        header.sortable ? "sortable" : null
                    } sort-direction-changing-header-flex`}
                >
                    {header.lable}
                </div>
            </th>
        );
    }

    const rowRender = (task: ITaskDisplay) => {
        return (
            <tr>
                <td>
                    <Link
                        to={`${archivedPage ? "/archive" : ""}/task/${task.id}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                    >
                        {task.idForView}
                    </Link>
                </td>
                <td className={openOnFullWindow ? "name_colum--full__window" : "name_colum"}>
                    <TaskLink task={task} url={`${archivedPage ? "/archive" : ""}/task/${task.id}`} />
                </td>
                <td>{taskStatusDisplay(task)}</td>
                <td>{taskColumnDisplay(task)}</td>
                <td>{taskPriorityDisplay(task)}</td>
                {openOnFullWindow && <td>{taskTypeDisplay(task)}</td>}
                <td>{personRowDisplay(task)}</td>
                {openOnFullWindow ? (
                    <td>{task.startDate ? dateToNiceString(new Date(task.startDate), "dd.MM.yyyy") : "Не указана"}</td>
                ) : null}
            </tr>
        );
    };

    return (
        <div
            className={
                openOnFullWindow ? "widget_wrapper--full__window" : "wigget-tasks-test main_page_widget widget_wrapper"
            }
            ref={containerRef}
            id="main-task-widget"
        >
            <div
                className={openOnFullWindow ? "widget_wrapper-full__window--block" : ""}
                style={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
                {hidden && (
                    <div className="full-screen-overlay">
                        <div className="popup-form-background" onClick={(e) => e.stopPropagation()}>
                            <TaskAddEditForm
                                mode="create"
                                taskId={undefined}
                                closeFunc={() => setHidden(false)}
                                projectId={Number(id)}
                                onTaskAction={onTaskAction}
                                responsibleRequired={projectSettings.responsibleRequired}
                                optionsQueryParams={{
                                    page: "project",
                                    entity: "task-form",
                                    action: "create",
                                    entityId: undefined,
                                    cheklistId: undefined,
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className={"widgets_header--container"}>
                    {openOnFullWindow ? (
                        <div onClick={() => setOpenOnFullWindow(false)} className="widgets_header--open__block">
                            <img src={CloseBlockWindow} />
                        </div>
                    ) : (
                        <div onClick={() => setOpenOnFullWindow(true)} className="widgets_header--open__block">
                            <img src={OpenBlock} />
                        </div>
                    )}
                </div>
                <div className="widjet_content-block">
                    <div className="widgets_header">
                        <div className="widgets_header--header__block">
                            <h2 className="widget-card-header-style">Задачи</h2>
                        </div>
                        <div className="widjets_header_rigth">
                            <Search
                                value={taskSearchText}
                                onSubmitFunc={onSearchSubmit}
                                changeSearchValue={setTaskSearchText}
                            />
                            <FilterContainer
                                filterCounter={counterTasks}
                                setFilterCounter={setCounterTasks}
                                selectedOptions={selectedOptionsTask}
                                setSelectedOptions={setSelectedOptionsTask}
                                filterOptions={filterOptionsTask}
                                showResetButton={taskSearchText.length > 0}
                                onReset={onCancelClick}
                                showApplyButton={true}
                                onApply={() => applyFilters()}
                            />
                            <AddButton onClickFunc={onAddClick} type={"task"} permission={addTask} />
                        </div>
                    </div>
                    <div
                        ref={curRef}
                        onScroll={onScroll}
                        className={openOnFullWindow ? "custom_table--full__window" : "custom_table"}
                    >
                        <table>
                            <thead className="custom_table_thead task_project_table">
                                <tr>
                                    {headers.map((header) => {
                                        return (
                                            <React.Fragment key={`task-row-head-${header.code}`}>
                                                {openOnFullWindow || !header.showOnlyOnFullScreen
                                                    ? columnHead(header)
                                                    : null}
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {viewTasks &&
                                    tasks.map((task) => {
                                        return (
                                            <React.Fragment key={`task-row-body-${task.id}`}>
                                                {rowRender(task)}
                                            </React.Fragment>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Tasks;
